"use client";

import { useTransition } from "react";

import { requestVerificationCode } from "@/app/(portal)/(unauthenticated)/(forms)/actions";
import { VerificationMethod } from "@/app/(portal)/(unauthenticated)/(forms)/types";
import { maskPhone } from "@/components/lib/masks";
import ApplicationSectionHeader from "@/components/ui/application-section-header";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/hooks/use-toast";

type OnRequested = (details: { method: VerificationMethod }) => void;

export default function Form({
  phoneNumber,
  email,
  onClickReset,
  onRequested,
}: {
  phoneNumber?: string;
  email?: string;
  onClickReset: () => void;
  onRequested?: OnRequested;
}) {
  const { toast } = useToast();

  const [isPending, startTransition] = useTransition();

  const handleSubmit = async (
    method: VerificationMethod,
    evt: React.FormEvent<HTMLFormElement>
  ) => {
    evt.preventDefault();
    startTransition(async () => {
      const recipient = method === "sms" ? phoneNumber : email;
      try {
        await requestVerificationCode(method, recipient!);
        onRequested?.({
          method,
        });
        toast({
          title: `Verification code sent to ${recipient}`,
        });
      } catch (e) {
        toast({
          title: "There was an error sending the verification code",
          variant: "destructive",
        });
        console.error(e);
      }
    });
  };

  return (
    <>
      <ApplicationSectionHeader
        title="Let's figure this out"
        description="If you haven't received a code to verify your account, there's a couple things we can do"
      />
      {phoneNumber && (
        <Info title={maskPhone(phoneNumber)}>
          <form onSubmit={handleSubmit.bind(null, "sms")}>
            <Button
              type="submit"
              size="sm"
              className="w-full"
              disabled={isPending}
            >
              Send a New Code via Text
            </Button>
          </form>
        </Info>
      )}
      {email && (
        <Info title={email}>
          <form onSubmit={handleSubmit.bind(null, "email")}>
            <Button
              data-testid="send-otp-email-button"
              type="submit"
              size="sm"
              className="w-full"
              disabled={isPending}
            >
              Send a New Code via Email
            </Button>
          </form>
        </Info>
      )}
      <Info title="My information is incorrect...">
        <Button size="sm" className="w-full" onClick={onClickReset}>
          Start Over
        </Button>
      </Info>
    </>
  );
}

function Info({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-containers flex flex-col gap-2 border p-3">
      <p className="text-center text-[14px] font-normal">{title}</p>
      {children}
    </div>
  );
}
